import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";

const S = {
  subtitle: { color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 24 },
  topRow: { display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" },
  addBtn: {
    background: "linear-gradient(135deg, #0A84FF, #0066CC)",
    color: "#fff", border: "none", borderRadius: 12,
    padding: "10px 20px", fontWeight: 600, fontSize: 14,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
  },
  joinBtn: {
    background: "rgba(255,255,255,0.08)", color: "#fff",
    border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12,
    padding: "10px 20px", fontWeight: 600, fontSize: 14,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  card: (isOwner) => ({
    background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
    border: `1px solid ${isOwner ? "rgba(10,132,255,0.3)" : "rgba(255,255,255,0.08)"}`,
    borderRadius: 16, padding: 20, cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s",
  }),
  cardName: { fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 6 },
  cardDesc: { fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 12, lineHeight: 1.4 },
  cardMeta: { display: "flex", gap: 16, fontSize: 12, color: "rgba(255,255,255,0.35)" },
  cardCode: {
    background: "rgba(10,132,255,0.15)", color: "#0A84FF",
    borderRadius: 8, padding: "4px 10px", fontSize: 13, fontWeight: 700,
    letterSpacing: "0.1em", fontFamily: "monospace",
  },
  badge: (active) => ({
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
    padding: "3px 8px", borderRadius: 6,
    background: active ? "rgba(48,209,88,0.15)" : "rgba(255,255,255,0.08)",
    color: active ? "#30D158" : "rgba(255,255,255,0.4)",
  }),
  empty: { textAlign: "center", padding: "60px 20px" },
  emptyIcon: { fontSize: 48, color: "rgba(255,255,255,0.08)", marginBottom: 16 },
  emptyText: { color: "rgba(255,255,255,0.3)", fontSize: 14, fontWeight: 500 },
  emptyHint: { color: "rgba(255,255,255,0.18)", fontSize: 12, marginTop: 6 },
  joinOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
  },
  joinCard: {
    background: "rgba(18,18,32,0.97)", backdropFilter: "blur(20px)",
    border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 20,
    padding: 24, width: "90%", maxWidth: 400,
  },
  joinTitle: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  joinCloseBtn: {
    background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8,
    width: 32, height: 32, color: "rgba(255,255,255,0.5)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
  },
  joinSubmitBtn: {
    background: "linear-gradient(135deg, #5eaa46, #4a9438)",
    color: "#fff", border: "none", borderRadius: 14,
    padding: "12px 0", fontWeight: 700, fontSize: 15,
    cursor: "pointer", width: "100%", marginTop: 8,
  },
};

function SharedEventsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinName, setJoinName] = useState("");
  const [joinError, setJoinError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const data = await EventsService.listSharedEvents();
      setEvents(data || []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setJoinError(null);
    try {
      const result = await EventsService.joinSharedEvent(joinCode.trim().toUpperCase(), joinName.trim() || "Player");
      setShowJoin(false);
      setJoinCode("");
      setJoinName("");
      navigate(`/shared-event/${result.sharedEventId || result.id}`);
    } catch (err) {
      setJoinError(err?.message || t("sharedEvents.invalidCode"));
    }
  };

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }}>
              <i className="fas fa-bars"></i>
            </a>
            <h5><i className="fas fa-users" style={{ marginRight: 8 }}></i>{t("sharedEvents.title")}</h5>
          </div>
        </nav>
      </div>

      <div className="main-wrapper">
        <div className="create-event-container">
          <p style={S.subtitle}>
            {t("sharedEvents.subtitle")}
          </p>

          <div style={S.topRow}>
            <button style={S.addBtn} onClick={() => navigate("/create-shared-event")}>
              <i className="fas fa-plus"></i> {t("sharedEvents.createEvent")}
            </button>
            <button style={S.joinBtn} onClick={() => setShowJoin(true)}>
              <i className="fas fa-sign-in-alt"></i> {t("sharedEvents.joinWithCode")}
            </button>
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <div className="spinner-border" role="status"><span className="visually-hidden">{t("common.loading")}</span></div>
            </div>
          ) : events.length === 0 ? (
            <div style={S.empty}>
              <div style={S.emptyIcon}><i className="fas fa-users"></i></div>
              <div style={S.emptyText}>{t("sharedEvents.noEventsYet")}</div>
              <div style={S.emptyHint}>{t("sharedEvents.createOrJoinHint")}</div>
            </div>
          ) : (
            <div style={S.grid}>
              {events.map((ev) => (
                <div
                  key={ev.id}
                  style={S.card(ev.isOwner)}
                  onClick={() => navigate(`/shared-event/${ev.id}`)}
                  onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.2)"; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={S.cardName}>{ev.name}</div>
                    <span style={S.badge(ev.started && !ev.finished)}>
                      {ev.finished ? t("common.ended") : ev.started ? t("common.live") : t("common.draft")}
                    </span>
                  </div>
                  {ev.description && <div style={S.cardDesc}>{ev.description}</div>}
                  <div style={S.cardMeta}>
                    <span><i className="fas fa-users" style={{ marginRight: 4 }}></i>{ev.participants?.length || 0} {t("sharedEvents.players")}</span>
                    <span><i className="fas fa-gamepad" style={{ marginRight: 4 }}></i>{ev.actions?.length || 0} {t("common.actions")}</span>
                    <span style={S.cardCode}>{ev.code}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Join Modal */}
      {showJoin && (
        <div style={S.joinOverlay} onClick={(e) => { if (e.target === e.currentTarget) setShowJoin(false); }}>
          <div style={S.joinCard}>
            <div style={S.joinTitle}>
              <span style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>{t("sharedEvents.joinEvent")}</span>
              <button style={S.joinCloseBtn} onClick={() => setShowJoin(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <label className="create-event-label">{t("sharedEvents.invitationCode")}</label>
            <input
              type="text"
              className="form-control create-event-name-input"
              placeholder={t("sharedEvents.codePlaceholder")}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              autoFocus
              maxLength={8}
              style={{ marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.15em", textAlign: "center", fontSize: 24, fontWeight: 800 }}
            />
            <label className="create-event-label">{t("sharedEvents.displayName")}</label>
            <input
              type="text"
              className="form-control create-event-name-input"
              placeholder={t("sharedEvents.displayNamePlaceholder")}
              value={joinName}
              onChange={(e) => setJoinName(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            {joinError && <p style={{ color: "#FF453A", fontSize: 13, marginBottom: 12 }}>{joinError}</p>}
            <button style={S.joinSubmitBtn} onClick={handleJoin}>
              <i className="fas fa-sign-in-alt" style={{ marginRight: 8 }}></i>
              {t("sharedEvents.joinBtn")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default SharedEventsPage;
